import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";

const JOB_STATUS_OPTIONS = [
  { value: "", label: "All Job Statuses" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
];

const APPROVAL_OPTIONS = [
  { value: "", label: "All Approval Statuses" },
  { value: "pending", label: "Pending Approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const getApprovalBadgeClass = (status) => {
  if (status === "approved") return "bg-success text-white";
  if (status === "rejected") return "bg-danger text-white";
  return "bg-warning text-dark";
};

const getJobStatusBadgeClass = (status) => {
  if (status === "active") return "bg-success-subtle text-success";
  if (status === "closed") return "bg-dark text-white";
  return "bg-secondary-subtle text-dark";
};

const AdminJobsPage = ({ approvalsOnly = false }) => {
  const location = useLocation();
  const recruiterIdFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("recruiter") || "",
    [location.search]
  );

  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState(recruiterIdFromQuery);
  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [approvalFilter, setApprovalFilter] = useState(
    approvalsOnly ? "pending" : ""
  );
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", "10");
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      if (recruiterFilter) params.set("recruiterId", recruiterFilter);
      if (jobStatusFilter) params.set("status", jobStatusFilter);
      if (approvalFilter) params.set("approvalStatus", approvalFilter);

      const [jobsResponse, recruitersResponse] = await Promise.all([
        axiosInstance.get(`/job-portal/jobs?${params.toString()}`, authConfig),
        axiosInstance.get("/job-portal/admin/users?role=recruiter&limit=100", authConfig),
      ]);

      setJobs(jobsResponse.data?.data?.jobs || []);
      setMeta(jobsResponse.data?.meta || { total: 0, totalPages: 1 });
      setRecruiters(recruitersResponse.data?.data?.users || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [page, recruiterFilter, jobStatusFilter, approvalFilter, searchTerm]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
  };

  const updateJob = async (jobId, payload, successMessage) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.put(`/job-portal/jobs/${jobId}`, payload, authConfig);
      toast.success(successMessage);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update job");
    }
  };

  const summary = useMemo(() => {
    return {
      total: jobs.length,
      active: jobs.filter((job) => job.jobStatus === "active").length,
      pending: jobs.filter((job) => job.adminApprovalStatus === "pending").length,
      approved: jobs.filter((job) => job.adminApprovalStatus === "approved").length,
      rejected: jobs.filter((job) => job.adminApprovalStatus === "rejected").length,
    };
  }, [jobs]);

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">
                {approvalsOnly ? "Job Approvals" : "All Jobs"}
              </h4>
              <p className="text-muted small mb-0">
                Moderate recruiter jobs, approval workflow, and platform job visibility.
              </p>
            </div>
            <Link to="/dashboard" className="btn btn-outline-primary">
              Back to Dashboard
            </Link>
          </div>

          <div className="row g-3 mb-4">
            {[
              { label: "Visible List", value: summary.total },
              { label: "Active", value: summary.active },
              { label: "Pending", value: summary.pending },
              { label: "Approved", value: summary.approved },
              { label: "Rejected", value: summary.rejected },
            ].map((card) => (
              <div className="col-md" key={card.label}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-muted">{card.label}</div>
                    <div className="fs-4 fw-bold">{loading ? "--" : card.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSearchSubmit}>
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Search by job title, company, department, city"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={recruiterFilter}
                    onChange={(e) => {
                      setRecruiterFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Recruiters</option>
                    {recruiters.map((recruiter) => (
                      <option key={recruiter.id} value={recruiter.id}>
                        {recruiter.name || recruiter.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={jobStatusFilter}
                    onChange={(e) => {
                      setJobStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    {JOB_STATUS_OPTIONS.map((option) => (
                      <option key={option.label} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={approvalFilter}
                    onChange={(e) => {
                      setApprovalFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    {APPROVAL_OPTIONS.map((option) => (
                      <option key={option.label} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-1">
                  <button className="btn btn-primary w-100" type="submit">
                    Go
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Job</th>
                    <th>Recruiter</th>
                    <th>Location</th>
                    <th>Approval</th>
                    <th>Job Status</th>
                    <th>Applications</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        Loading jobs...
                      </td>
                    </tr>
                  ) : jobs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        No jobs found.
                      </td>
                    </tr>
                  ) : (
                    jobs.map((job) => (
                      <tr key={job.id}>
                        <td>
                          <div className="fw-semibold">{job.title || "-"}</div>
                          <div className="text-muted small">
                            {job.department || job.category || "-"}
                          </div>
                        </td>
                        <td>
                          <div>{job.recruiter?.name || "-"}</div>
                          <div className="text-muted small">
                            {job.company?.name || job.recruiter?.email || "-"}
                          </div>
                        </td>
                        <td>{job.location?.display || "-"}</td>
                        <td>
                          <span
                            className={`badge text-capitalize ${getApprovalBadgeClass(
                              job.adminApprovalStatus || "pending"
                            )}`}
                          >
                            {job.adminApprovalStatus || "pending"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge text-capitalize ${getJobStatusBadgeClass(
                              job.jobStatus || "draft"
                            )}`}
                          >
                            {job.jobStatus || "draft"}
                          </span>
                        </td>
                        <td>{job.applicationCount || 0}</td>
                        <td>
                          {job.createdAt
                            ? new Date(job.createdAt).toLocaleDateString("en-IN")
                            : "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            <Link to={`/job-view/${job.id}`} className="btn btn-sm btn-outline-primary">
                              View
                            </Link>
                            <button
                              type="button"
                              className={
                                job.adminApprovalStatus === "approved"
                                  ? "btn btn-sm btn-success"
                                  : "btn btn-sm btn-outline-success"
                              }
                              disabled={job.adminApprovalStatus === "approved"}
                              onClick={() =>
                                updateJob(
                                  job.id,
                                  { adminApprovalStatus: "approved", jobStatus: "active", status: 1 },
                                  "Job approved"
                                )
                              }
                            >
                              {job.adminApprovalStatus === "approved"
                                ? "Approved"
                                : "Approve"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                updateJob(
                                  job.id,
                                  { adminApprovalStatus: "rejected", jobStatus: "draft", status: 0 },
                                  "Job rejected"
                                )
                              }
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() =>
                                updateJob(
                                  job.id,
                                  {
                                    jobStatus:
                                      job.jobStatus === "closed" ? "active" : "closed",
                                    status: job.jobStatus === "closed" ? 1 : 0,
                                  },
                                  "Job status updated"
                                )
                              }
                            >
                              {job.jobStatus === "closed" ? "Activate" : "Close"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-body border-top d-flex justify-content-between align-items-center">
              <span className="text-muted small">
                Page {page} of {meta.totalPages || 1}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= (meta.totalPages || 1)}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AdminJobsPage;
